import { useCallback, useState } from "react";

import { type Tos, TosKey } from "../../../gql-generated/types";
import { type TosSource } from "../../components/tos";
import { useSetTos } from "../useSetTos";
import { useTos } from "../useTos";

interface UseMonetizationTosReturn {
    loading: boolean;
    tos: Tos | undefined;
    acceptedLatestVersion: boolean;
    acceptedOldVersion: boolean;
    tosModalVisible: boolean;
    tosSource: TosSource | null;
    triggerTosModal: (source: TosSource) => void;
    closeTosModal: () => void;
    triggerTosAcceptance: () => Promise<void>;
}

export const useMonetizationTos = (): UseMonetizationTosReturn => {
    const { loading: tosLoading, tos, acceptedLatestVersion, acceptedOldVersion } = useTos(TosKey.LensCreatorPayoutTos);
    const { loading: setTosLoading, setTosLatestAcceptedVersion } = useSetTos(TosKey.LensCreatorPayoutTos);

    const [tosModalVisible, setTosModalVisible] = useState(false);
    const [tosSource, setTosSource] = useState<TosSource | null>(null);

    const triggerTosModal = useCallback((source: TosSource) => {
        setTosSource(source);
        setTosModalVisible(true);
    }, []);

    const closeTosModal = useCallback(() => {
        setTosModalVisible(false);
        setTosSource(null);
    }, []);

    const triggerTosAcceptance = useCallback(async () => {
        try {
            await setTosLatestAcceptedVersion();
            setTosModalVisible(false);
            setTosSource(null);
        } catch (error) {
            console.error("Failed to accept TOS:", error);
        }
    }, [setTosLatestAcceptedVersion]);

    return {
        loading: tosLoading || setTosLoading,
        tos,
        acceptedLatestVersion,
        acceptedOldVersion,
        tosModalVisible,
        tosSource,
        triggerTosModal,
        closeTosModal,
        triggerTosAcceptance,
    };
};
